import { observable, set, action, runInAction, configure } from "mobx";
import {
	TProductModule,
	TProductElement,
	GetProductsResp,
	LoadPoolProps,
} from "../../../services/product";
import { isNotBlankArray } from "utils/array";
import { GET_PRODUCTS_LIMIT } from "utils/playground/product";

configure({ isolateGlobalState: true });
export default class MProductsStore {
	@observable public productsListMap: Record<string, TProductElement[]> = {};
	@observable public productsLimit: Record<string, number> = {};
	@observable public isLoadEnd: Record<string, boolean> = {};
	@observable private productsLoadMore: Record<string, boolean> = {};
	@observable public viewMoreLink: Record<string, string> = {};
	@observable public isLoadingProducts: Record<string, boolean> = {};
	@observable private offset: Record<string, number> = {};
	/** 已被ctr曝光商品的最后一位索引号 + 1 */
	@observable public ctrShowPosition: number = 0;
	/** 记录已经被曝光的产品gid */
	@observable public exposureProductListMap: Record<string, string[]> = {};

	constructor(modules: TProductModule[] = []) {
		modules.forEach((item) => {
			const hasElements = isNotBlankArray(item.productElements);
			const mapKey = `${item.poolId}`;

			// set product elements by poolId
			set(this.productsListMap, {
				[mapKey]: item.productElements || [],
			});
			// 设置商品池是否可以无限加载
			set(this.productsLoadMore, { [mapKey]: item.canLoadMore });
			const maxLimit = item.maxLoad;

			// 初始化已请求数量
			set(this.offset, {
				[mapKey]: hasElements ? Math.min(GET_PRODUCTS_LIMIT, maxLimit) : 0,
			});
			set(this.isLoadEnd, {
				[mapKey]: hasElements ? item.productElements.length === maxLimit : false,
			});

			if (!item.canLoadMore) {
				// 设置后台设置的商品展示数量
				set(this.productsLimit, { [mapKey]: maxLimit });

				set(this.viewMoreLink, { [mapKey]: item.link || "" });
			} else {
				// 如果为无限加载，则设置单次请求数量为 20
				set(this.productsLimit, { [mapKey]: GET_PRODUCTS_LIMIT });
			}
		});
	}

	@action
	handleInvalidLoadStatus = (tabKey: string) => {
		if (!tabKey || this.isLoadEnd[tabKey] || this.isLoadingProducts[tabKey]) {
			return true;
		}
		return false;
	};

	@action
	getLimit = (poolId: string) => {
		if (this.productsLoadMore[poolId]) {
			return this.productsLimit[poolId];
		}
		const productLimit: number =
			this.productsLimit[poolId] - (this.productsListMap[poolId]?.length || 0);
		return Math.min(productLimit, GET_PRODUCTS_LIMIT);
	};
	/**
	 * 商品请求思路
	 * 有loadmore, 每页Math.min(20, maxload)条翻页
	 * 无loadmore, 最多请求maxload, limit = Math.min([...maxload, 20, maxload - prePage * 20])
	 * @param onLoadPool
	 */
	@action
	loadProductsList = async (
		poolId: string,
		onLoadPool: (props: LoadPoolProps) => Promise<GetProductsResp>,
	) => {
		if (this.handleInvalidLoadStatus(poolId)) {
			return;
		}
		set(this.isLoadingProducts, { [poolId]: true });

		try {
			const realLimit: number = this.getLimit(poolId);
			const reqLimit = Math.max(realLimit, GET_PRODUCTS_LIMIT);
			const products = this.productsListMap[poolId] || [];
			const resp = await onLoadPool({
				poolId,
				limit: `${reqLimit}`,
				offset: (this.offset[poolId] || 0).toString(),
				prmPosition: products.length,
			});

			if (isNotBlankArray(resp?.products)) {
				runInAction("update product list", () => {
					// 商品已请求数量 = 累计请求数量 + 本次请求数量
					set(this.offset, {
						[poolId]: this.offset[poolId] + realLimit,
					});
					let resProducts = resp.products;
					if (resProducts.length > realLimit) {
						resProducts = resProducts.slice(0, realLimit);
					}
					set(this.productsListMap, { [poolId]: [...products, ...resProducts] });

					/**
					 * 不再加载的触发条件：
					 * 1. 接口返回的 hasNext 为 false 时
					 * 2. 在商品池非无限加载的情况下，接口返回的商品数量累计值 >= 商品池上限
					 */
					if (
						!resp.hasNext ||
						(this.productsListMap[poolId] &&
							this.productsListMap[poolId].length >= this.productsLimit[poolId] &&
							!this.productsLoadMore[poolId])
					) {
						set(this.isLoadEnd, { [poolId]: true });
					}
				});
			} else {
				runInAction(() => {
					set(this.isLoadEnd, { [poolId]: true });
				});
			}
			return resp.products;
		} catch (err) {
			console.error("GetProducts Got error", err);
		} finally {
			runInAction(() => {
				set(this.isLoadingProducts, { [poolId]: false });
			});
		}
		return [];
	};

	@action
	handleExposureProductList = (mapKey: string, productList: string[]) => {
		if (mapKey) {
			set(this.exposureProductListMap, { [mapKey]: productList });
		}
	};
}
